/**
 * Director Studio API Client
 * Handles communication with the Director Studio endpoints from foodie-xanadu API
 */

import { authService } from './authService';
import { buildOrgScopedVideoPath, withOrgHeader } from './apiContext';

const LOGIN_URL = import.meta.env.VITE_LOGIN_URL || 'http://localhost:8000/login.dev.html';
const COMPLETED_VIDEO_STATES = new Set(['completed', 'done', 'ready', 'success', 'succeeded']);

const normalizeMediaUrl = (baseUrl, url) => {
  if (typeof url !== 'string' || !url) return '';
  if (
    url.startsWith('http://')
    || url.startsWith('https://')
    || url.startsWith('blob:')
    || url.startsWith('data:')
  ) {
    return url;
  }
  if (url.startsWith('/')) {
    return `${baseUrl}${url}`;
  }
  return url;
};

const normalizeVideoAssets = (baseUrl, jobId, state, completedAt, assets = {}) => {
  const normalizedAssets = { ...(assets || {}) };
  const isCompleted =
    COMPLETED_VIDEO_STATES.has(String(state || '').toLowerCase())
    || Boolean(completedAt);

  normalizedAssets.video_url = normalizeMediaUrl(baseUrl, normalizedAssets.video_url);
  normalizedAssets.thumbnail_url = normalizeMediaUrl(baseUrl, normalizedAssets.thumbnail_url);

  if (!normalizedAssets.video_url && jobId && isCompleted) {
    normalizedAssets.video_url = `${baseUrl}${buildOrgScopedVideoPath(jobId, '/asset')}`;
  }
  if (!normalizedAssets.thumbnail_url && jobId && isCompleted) {
    normalizedAssets.thumbnail_url = `${baseUrl}${buildOrgScopedVideoPath(jobId, '/thumbnail')}`;
  }

  return normalizedAssets;
};

class DirectorApiError extends Error {
  constructor(message, status = null, details = null) {
    super(message);
    this.name = 'DirectorApiError';
    this.status = status;
    this.details = details;
  }
}

export class DirectorApiClient {
  constructor(baseUrl = null) {
    this.baseUrl = baseUrl || import.meta.env.VITE_VIDEO_API_BASE_URL || 'http://localhost:5001';
    this.headers = {
      'Content-Type': 'application/json',
    };
  }

  async _makeRequest(path, options = {}, { includeOrg = true } = {}) {
    const url = `${this.baseUrl}${path}`;
    const method = (options.method || 'GET').toUpperCase();

    // For GET requests, prevent browser caching so we always get fresh data
    let headers = { ...this.headers, ...options.headers };
    if (method === 'GET') {
      headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
      headers['Pragma'] = 'no-cache';
    }
    headers = withOrgHeader(headers, { includeOrg });

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include', // Required for session cookies
      });

      // Handle 401 Unauthorized - redirect to login
      if (response.status === 401) {
        console.warn('Unauthorized - redirecting to login');
        authService.handleUnauthorized();
        throw new DirectorApiError('Unauthorized', 401, { redirect: true });
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new DirectorApiError(
          errorData.detail || errorData.message || `HTTP ${response.status}`,
          response.status,
          errorData
        );
      }

      if (response.status === 204) return null;
      return await response.json();
    } catch (error) {
      if (error instanceof DirectorApiError) {
        throw error;
      }
      throw new DirectorApiError(`Network error: ${error.message}`, null, error);
    }
  }

  /**
   * Bootstrap a Director Studio project from a place_id or website URL
   * POST /api/v1/director/jobs
   * @param {Object} source - Source identifier: { place_id: string } or { website_url: string }
   * @param {Object} [options] - Additional options
   * @param {string} [options.advertiser_id] - Advertiser ID to associate with the project
   */
  async createProject(source, options = {}) {
    const body = {
      source,
      metadata: {
        requested_by: 'cornercast-director-studio',
        locale: 'en-US'
      }
    };
    if (options.advertiser_id) {
      body.advertiser_id = options.advertiser_id;
    }
    return this._makeRequest('/api/v1/director/jobs', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /**
   * Get current BusinessData for a project
   * GET /api/v1/director/jobs/{job_id}/business
   */
  async getBusinessData(jobId) {
    return this._makeRequest(`/api/v1/director/jobs/${jobId}/business`);
  }

  /**
   * Update BusinessData with partial changes
   * PATCH /api/v1/director/jobs/{job_id}/business
   */
  async updateBusinessData(jobId, updates) {
    return this._makeRequest(`/api/v1/director/jobs/${jobId}/business`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  /**
   * Upload a new photo via data URL
   * POST /api/v1/director/jobs/{job_id}/photos
   */
  async uploadPhoto(jobId, dataUrl, filename = 'uploaded_image.jpg') {
    return this._makeRequest(`/api/v1/director/jobs/${jobId}/photos`, {
      method: 'POST',
      body: JSON.stringify({
        data_url: dataUrl,
        filename: filename
      }),
    });
  }

  /**
   * Remove an uploaded photo
   * DELETE /api/v1/director/jobs/{job_id}/photos/{photo_id}
   */
  async deletePhoto(jobId, photoId) {
    return this._makeRequest(`/api/v1/director/jobs/${jobId}/photos/${encodeURIComponent(photoId)}`, {
      method: 'DELETE',
    });
  }

  /**
   * Generate script from business data
   * POST /api/v1/director/jobs/{job_id}/script:generate
   */
  async generateScript(jobId, options = {}) {
    return this._makeRequest(`/api/v1/director/jobs/${jobId}/script:generate`, {
      method: 'POST',
      body: JSON.stringify({
        campaign: options.campaign || {},
        metadata: options.metadata || {}
      }),
    });
  }

  /**
   * Get current script/advert
   * GET /api/v1/director/jobs/{job_id}/script
   */
  async getScript(jobId) {
    return this._makeRequest(`/api/v1/director/jobs/${jobId}/script`);
  }

  /**
   * Update individual scene in script
   * PATCH /api/v1/director/jobs/{job_id}/script/scenes/{scene_idx}
   */
  async updateScene(jobId, sceneIndex, sceneUpdates) {
    return this._makeRequest(`/api/v1/director/jobs/${jobId}/script/scenes/${sceneIndex}`, {
      method: 'PATCH',
      body: JSON.stringify(sceneUpdates),
    });
  }

  /**
   * Generate initial frame for a scene
   * POST /api/v1/director/jobs/{job_id}/scenes/{scene_idx}/initial-frame:generate
   */
  async generateInitialFrame(jobId, sceneIndex, force = true) {
    return this._makeRequest(`/api/v1/director/jobs/${jobId}/scenes/${sceneIndex}/initial-frame:generate`, {
      method: 'POST',
      body: JSON.stringify({ force }),
    });
  }

  /**
   * Generate slide image for a SlideScene
   * POST /api/v1/director/jobs/{job_id}/scenes/{scene_idx}/slide:generate
   */
  async generateSlide(jobId, sceneIndex, force = false) {
    return this._makeRequest(`/api/v1/director/jobs/${jobId}/scenes/${sceneIndex}/slide:generate`, {
      method: 'POST',
      body: JSON.stringify({ force }),
    });
  }

  /**
   * Generate video for a specific scene
   * POST /api/v1/director/jobs/{job_id}/scenes/{scene_idx}/video:generate
   */
  async generateSceneVideo(jobId, sceneIndex) {
    return this._makeRequest(`/api/v1/director/jobs/${jobId}/scenes/${sceneIndex}/video:generate`, {
      method: 'POST',
    });
  }

  /**
   * Generate complete video for the project
   * POST /api/v1/director/jobs/{job_id}/video:generate
   */
  async generateFullVideo(jobId) {
    return this._makeRequest(`/api/v1/director/jobs/${jobId}/video:generate`, {
      method: 'POST',
    });
  }

  /**
   * Get job status and video assets
   * GET /api/v1/videos/{job_id}
   */
  async getJobStatus(jobId) {
    const data = await this._makeRequest(`/api/v1/videos/${jobId}`);
    const resolvedJobId = data?.job_id || data?.jobId || jobId;
    const completedAt = data?.completed_at || data?.completedAt;
    const assets = normalizeVideoAssets(
      this.baseUrl,
      resolvedJobId,
      data?.state || data?.status,
      completedAt,
      data?.assets,
    );
    const rootVideoUrl = normalizeMediaUrl(this.baseUrl, data?.video_url);
    const rootThumbnailUrl = normalizeMediaUrl(this.baseUrl, data?.thumbnail_url);

    if (!assets.video_url && rootVideoUrl) {
      assets.video_url = rootVideoUrl;
    }
    if (!assets.thumbnail_url && rootThumbnailUrl) {
      assets.thumbnail_url = rootThumbnailUrl;
    }

    return {
      ...data,
      job_id: resolvedJobId,
      jobId: resolvedJobId,
      video_url: rootVideoUrl || assets.video_url || '',
      thumbnail_url: rootThumbnailUrl || assets.thumbnail_url || '',
      assets,
      completed_at: completedAt,
      completedAt,
    };
  }

  /**
   * Get video asset URL. Uses the organization-scoped path so it can be
   * placed on a native <video> element (which cannot send custom headers).
   * GET /api/v1/organizations/{org_id}/videos/{job_id}/asset
   */
  getVideoAssetUrl(jobId) {
    return `${this.baseUrl}${buildOrgScopedVideoPath(jobId, '/asset')}`;
  }

  /**
   * Fetch the status of an async generation operation.
   * GET /api/v1/generation-operations/{operation_id}
   *
   * Used to poll the result of endpoints that return 202 Accepted with a
   * GenerationOperationAcceptedResponse body (initial-frame:generate,
   * slide:generate, scenes/.../video:generate, etc.).
   *
   * @param {string} operationId - The operation_id from the 202 response
   * @returns {Promise<Object>} Operation status. Notable fields:
   *   - state: 'requested' | 'launched' | 'running' | 'completed' | 'failed'
   *   - result_url: relative URL of the produced asset (when completed)
   *   - error_code, error_detail: populated when state === 'failed'
   */
  async getGenerationOperationStatus(operationId) {
    return this._makeRequest(`/api/v1/generation-operations/${operationId}`);
  }

  /**
   * Poll an async generation operation until it reaches a terminal state.
   * Resolves with the final operation status payload (state === 'completed' or 'failed').
   *
   * @param {string} operationId
   * @param {Object} [options]
   * @param {number} [options.intervalMs=2000] - Poll interval
   * @param {number} [options.timeoutMs=300000] - Max wait before rejecting (5 min default)
   * @param {function} [options.onUpdate] - Optional callback invoked with each status payload
   */
  async waitForOperation(operationId, options = {}) {
    const intervalMs = options.intervalMs ?? 2000;
    const timeoutMs = options.timeoutMs ?? 300000;
    const onUpdate = typeof options.onUpdate === 'function' ? options.onUpdate : null;
    const startedAt = Date.now();

    const TERMINAL = new Set(['completed', 'failed']);

    // Small async sleep helper
    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    /* eslint-disable no-await-in-loop */
    while (true) {
      const status = await this.getGenerationOperationStatus(operationId);
      if (onUpdate) {
        try { onUpdate(status); } catch (_e) { /* swallow listener errors */ }
      }
      if (status && TERMINAL.has(String(status.state || '').toLowerCase())) {
        return status;
      }
      if (Date.now() - startedAt > timeoutMs) {
        throw new DirectorApiError(
          `Generation operation ${operationId} did not finish within ${timeoutMs}ms`,
          null,
          { operationId, lastStatus: status }
        );
      }
      await sleep(intervalMs);
    }
    /* eslint-enable no-await-in-loop */
  }

  /**
   * Get video configuration (aspect ratio, logo overlay, etc.)
   * GET /api/v1/director/jobs/{job_id}/video-config
   * @param {string} jobId - Job ID
   * @returns {Promise<Object>} Video configuration
   * @returns {string} [return.aspect_ratio] - Aspect ratio ('16:9' or '9:16')
   * @returns {string} [return.script_mode] - Scripting mode ('creative' or 'grounded')
   * @returns {boolean} [return.logo_overlay_enabled] - Whether logo overlay is enabled
   * @returns {string} [return.logo_overlay_position] - Logo position ('auto', 'top-left', 'top-right', 'bottom-left', 'bottom-right')
   * @returns {number} [return.logo_overlay_opacity] - Logo opacity (0.0 to 1.0)
   */
  async getVideoConfig(jobId) {
    return this._makeRequest(`/api/v1/director/jobs/${jobId}/video-config`, {
      method: 'GET',
    });
  }

  /**
   * Update video configuration (aspect ratio, logo overlay, etc.)
   * PATCH /api/v1/director/jobs/{job_id}/video-config
   * @param {string} jobId - Job ID
   * @param {Object} videoConfig - Configuration to update
   * @param {string} [videoConfig.aspect_ratio] - Aspect ratio ('16:9' or '9:16')
   * @param {number} [videoConfig.length] - Video length in seconds (6, 15, or 30)
   * @param {string} [videoConfig.script_mode] - Scripting mode ('creative' or 'grounded')
   * @param {boolean} [videoConfig.logo_overlay_enabled] - Enable logo overlay on video
   * @param {string} [videoConfig.logo_overlay_position] - Logo position ('auto', 'top-left', 'top-right', 'bottom-left', 'bottom-right')
   * @param {number} [videoConfig.logo_overlay_opacity] - Logo opacity (0.0 to 1.0)
   * @returns {Promise<Object>} Updated configuration
   */
  async updateVideoConfig(jobId, videoConfig) {
    return this._makeRequest(`/api/v1/director/jobs/${jobId}/video-config`, {
      method: 'PATCH',
      body: JSON.stringify(videoConfig),
    });
  }

  /**
   * Refresh enrichment data (re-scrape business info, branding, etc.)
   * POST /api/v1/director/jobs/{job_id}/enrichment:refresh
   * @param {string} jobId - Job ID
   * @returns {Promise<Object>} Refreshed business data
   */
  async refreshEnrichment(jobId) {
    return this._makeRequest(`/api/v1/director/jobs/${jobId}/enrichment:refresh`, {
      method: 'POST',
    });
  }

  /**
   * Get campaign options (dropdown values for purpose, tone, channel, seasonal_theme)
   * GET /api/v1/director/config/campaign-options
   * @returns {Promise<Object>} Campaign options with purpose, tone, channel, seasonal_theme arrays
   */
  async getCampaignOptions() {
    return this._makeRequest('/api/v1/director/config/campaign-options');
  }

  /**
   * Get campaign brief for a project
   * GET /api/v1/director/jobs/{job_id}/campaign-brief
   * @param {string} jobId - Job ID
   * @returns {Promise<Object>} Campaign brief with purpose, tone, channel, offers, etc.
   */
  async getCampaignBrief(jobId) {
    return this._makeRequest(`/api/v1/director/jobs/${jobId}/campaign-brief`);
  }

  /**
   * Update campaign brief (partial update)
   * PATCH /api/v1/director/jobs/{job_id}/campaign-brief
   * @param {string} jobId - Job ID
   * @param {Object} briefUpdates - Fields to update
   * @param {string} [briefUpdates.purpose] - awareness, promotion, launch, event, seasonal, retention, other
   * @param {string} [briefUpdates.seasonal_theme] - new_year, valentine, spring, summer, back_to_school, halloween, thanksgiving, holiday, none
   * @param {string} [briefUpdates.tone] - auto, friendly, professional, playful, elegant, bold, humorous, luxe, minimalist, rugged, modern
   * @param {string} [briefUpdates.channel] - auto, social, ctv, dooh, youtube
   * @param {Array} [briefUpdates.offers] - Array of offer objects with headline, code, fine_print, valid_until
   * @returns {Promise<Object>} Updated campaign brief
   */
  async updateCampaignBrief(jobId, briefUpdates) {
    return this._makeRequest(`/api/v1/director/jobs/${jobId}/campaign-brief`, {
      method: 'PATCH',
      body: JSON.stringify(briefUpdates),
    });
  }

  /**
   * Upload a character reference image
   * POST /api/v1/director/jobs/{job_id}/characters
   */
  async uploadCharacter(jobId, { data_urls, name, role, description }) {
    return this._makeRequest(`/api/v1/director/jobs/${jobId}/characters`, {
      method: 'POST',
      body: JSON.stringify({ data_urls, name, role, description }),
    });
  }

  /**
   * Get all characters for a job
   * GET /api/v1/director/jobs/{job_id}/characters
   */
  async getCharacters(jobId) {
    return this._makeRequest(`/api/v1/director/jobs/${jobId}/characters`);
  }

  /**
   * Update a character's metadata
   * PATCH /api/v1/director/jobs/{job_id}/characters/{character_id}
   */
  async updateCharacter(jobId, characterId, updates) {
    return this._makeRequest(`/api/v1/director/jobs/${jobId}/characters/${characterId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  /**
   * Delete a character
   * DELETE /api/v1/director/jobs/{job_id}/characters/{character_id}
   */
  async deleteCharacter(jobId, characterId) {
    return this._makeRequest(`/api/v1/director/jobs/${jobId}/characters/${characterId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Upload or replace the business logo
   * PUT /api/v1/director/jobs/{job_id}/logo
   */
  async uploadLogo(jobId, dataUrl) {
    return this._makeRequest(`/api/v1/director/jobs/${jobId}/logo`, {
      method: 'PUT',
      body: JSON.stringify({ data_url: dataUrl }),
    });
  }

  /**
   * Delete the business logo
   * DELETE /api/v1/director/jobs/{job_id}/logo
   */
  async deleteLogo(jobId) {
    return this._makeRequest(`/api/v1/director/jobs/${jobId}/logo`, {
      method: 'DELETE',
    });
  }

  /**
   * Convert file to data URL for upload
   */
  async fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }
}

// Create singleton instance
export const directorApiClient = new DirectorApiClient();

// Export error class for error handling
export { DirectorApiError };
